import React, {
  useEffect,
  useState,
  Suspense,
  useCallback,
  useRef,
  useMemo,
  lazy,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageLoading from "./components/Loading.jsx";
import useSettings from "./hooks/useSettings";
import useAlign from "./hooks/useAlign";
import useLanguage from "./hooks/useLanguage";
import { ThemeProvider } from "./providers/ThemeProvider";

import { useRecoilState } from "recoil";
import { noRideIdAtom, settingsAtom, underMaintenanceAtom } from "./state.js";
import { ScrollProvider } from "./contexts/ScrollContext";
import Popview from "./components/Popview.jsx";

// Lazy-load components for better code splitting
const MainRoutes = lazy(() =>
  import("./components/routes/Routes.jsx").then((module) => ({
    default: module.MainRoutes,
  })),
);

// Lazy load PopUp component
const PopUp = lazy(() =>
  import("./components/common/Popup.jsx").then((module) => ({
    default: module.PopUp,
  })),
);

export default function App() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [loading, setLoading] = useState(true);
  const { fetchSettings } = useSettings();
  const { fetchLanguage } = useLanguage();
  const { alignLeft, alignRight } = useAlign();
  const [settingsRes] = useRecoilState(settingsAtom);
  const [noRideId, setNoRideId] = useRecoilState(noRideIdAtom);
  const [underMaintenance, setUnderMaintenance] =
    useRecoilState(underMaintenanceAtom);

  const [popup, setPop] = useState(null);

  // Use ref to track initialization
  const initialized = useRef(false);

  // Scroll to top on mount only
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Socket connection status logging
  // useEffect(() => {
  //   console.log(adminSocket, "lwkjrbh");
  // }, [adminSocket]);

  // Initialize app only once with optimized loading
  useEffect(() => {
    // Skip if already initialized or if settings exist in state
    if (initialized.current || settingsRes) {
      setLoading(false);
      return;
    }

    const initializeApp = async () => {
      try {
        // Mark as initialized immediately to prevent duplicate calls
        initialized.current = true;

        const { data } = await fetchSettings();

        //console.log(data, "ljkbhv");

        if (!data) {
          navigate("/under-maintenance");
          setLoading(false);
          return;
        }

        // Set alignment based on language direction
        data.languageDirection === "RTL" ? alignRight() : alignLeft();

        // Critical operations in parallel
        const criticalPromises = [];
        if (data?.languageCode) {
          criticalPromises.push(
            fetchLanguage(data.clientId, data.languageCode),
          );
        }

        // Wait for critical operations
        if (criticalPromises.length > 0) {
          await Promise.all(criticalPromises);
        }

        // Set loading to false early
        setLoading(false);
      } catch (error) {
        console.error("Failed to initialize app:", error);
        setLoading(false);
        navigate("/under-maintenance");
      }
    };

    initializeApp();

    // Empty dependency array ensures this runs only once on mount
  }, []);

  // Show loading indicator while initializing
  if (loading) {
    return <PageLoading />;
  }

  // Render main application once loaded
  return (
    <ThemeProvider>
      <ScrollProvider>
        <Suspense fallback={<PageLoading />}>
          <MainRoutes />
          {popup != null && (
            // <Suspense fallback={null}>
            <PopUp
              unmount={() => setPop(null)}
              title={popup.title}
              type={popup.type}
            />
            // </Suspense>
          )}
        </Suspense>
      </ScrollProvider>
    </ThemeProvider>
  );
}
